import { EyeIcon, EyeOffIcon } from "@heroicons/react/outline";
import clsx from "clsx";
import InputLabel from "components/InputLabel";
import React, { useState } from "react";

interface IProps {
  labelName?: string;
  value?: string;
  defaultValue?: string;
  type?: "string" | "number" | "boolean" | "url" | "email" | "password";
  onChange?: (value: string | undefined, name?: string) => void;
  onChangeNumber?: (value: number | undefined, name?: string) => void;
  onKeyup?: (
    value: string | undefined,
    e: string,
    validity?: ValidityState
  ) => void;
  onKeyDown?: (e: React.KeyboardEvent) => void;
  placeholder?: string;
  name?: string;
  className?: string;
  errorMessage?: string;
  typingError?: string;
  containerClassName?: string;
  noMargin?: boolean;
  noArrow?: boolean;
  size?: "large" | "middle" | "small";
  onBlur?: (
    e: React.FocusEvent<HTMLInputElement>,
    name: string,
    emptyRequired?: boolean
  ) => void;
  required?: boolean;
  infoText?: string;
  disable?: boolean;
  unit?: string;
  min?: number;
  max?: number;
  showMandatoryAlert?: boolean;
  testID?: string;
  inputDivClassName?: string;
  helper?: React.ReactNode;
  shouldShowValidationError?: (value?: string | null | undefined) => boolean;
  onFocus?: (e: React.FocusEvent<HTMLInputElement>) => void;
  onClick?: (e: React.MouseEvent) => void;
  onDoubleClick?: (e: React.MouseEvent) => void;
}

const Input = React.forwardRef(
  (
    {
      labelName,
      value,
      defaultValue,
      type = "string",
      onChange,
      onChangeNumber,
      onKeyup,
      placeholder,
      name = "",
      className,
      errorMessage,
      typingError,
      containerClassName,
      noMargin,
      noArrow,
      size = "middle",
      onBlur,
      required,
      infoText,
      disable,
      unit,
      min,
      max,
      showMandatoryAlert,
      testID,
      inputDivClassName,
      ...props
    }: IProps,
    ref: React.Ref<HTMLInputElement>
  ) => {
    const [visiblePassword, setVisiblePassword] = useState(false);
    const handleOnBlur = (e: React.FocusEvent<HTMLInputElement>) => {
      onBlur && onBlur(e, name, required && !value?.length);
    };

    const defaultShowValidationError = (value: string | null | undefined) => {
      return (
        required &&
        (!value?.length || value === "undefined" || value === "null") &&
        showMandatoryAlert
      );
    };

    const validate =
      props.shouldShowValidationError || defaultShowValidationError;
    const hasValidationError = validate(value);

    const handleKeyDown = (e: React.KeyboardEvent) => {
      if (Number(min) >= 0 && (e.key === "-" || e.key === "e")) {
        e.preventDefault();
      }
      props.onKeyDown?.(e);
    };

    return (
      <div
        className={clsx(`pb-15 relative ${containerClassName}`, {
          "mb-4": !noMargin,
        })}
      >
        {labelName ? (
          <InputLabel
            required={required}
            labelName={labelName ?? ""}
            name={name}
          />
        ) : null}
        <div className={clsx("relative flex items-center", inputDivClassName)}>
          <input
            ref={ref}
            defaultValue={defaultValue}
            type={visiblePassword ? "text" : type}
            className={clsx(
              "appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md",
              "placeholder:text-gray-500 placeholder:text-sm placeholder:font-normal",
              "focus:border-indigo-500 sm:text-sm appearance-none focus:outline-none",
              `${className} ${noArrow || unit ? "number-no-arrow" : ""}`,
              {
                "h-large": size === "large",
                "h-middle": size === "middle",
                "h-small": size === "small",
                "!border-red-500 placeholder:text-red-500": hasValidationError,
                "opacity-50": disable,
              }
            )}
            value={min && Number(value) < Number(min) ? "" : value}
            name={name}
            onChange={(e) => {
              if (onChange) onChange(e.target.value, e.target.name);
              if (onChangeNumber)
                onChangeNumber(e.target.valueAsNumber, e.target.name);
            }}
            placeholder={placeholder}
            onBlur={handleOnBlur}
            disabled={disable}
            onKeyUp={(e) =>
              onKeyup &&
              onKeyup(
                (e.target as HTMLInputElement).value,
                e.key,
                (e.target as HTMLInputElement).validity
              )
            }
            min={min}
            max={max}
            test-id={testID}
            onKeyDown={handleKeyDown}
            onFocus={props.onFocus}
            onClick={props.onClick}
            onDoubleClick={props.onDoubleClick}
          />
          {unit && (
            <span className="absolute right-4 text-sm text-gray-400">
              {unit}
            </span>
          )}
          {type === "password" &&
            (visiblePassword ? (
              <EyeOffIcon
                width={20}
                className="absolute right-3 text-gray-400"
                onClick={() => setVisiblePassword(false)}
              />
            ) : (
              <EyeIcon
                width={20}
                className="absolute right-3 text-gray-400"
                onClick={() => setVisiblePassword(true)}
              />
            ))}
        </div>
        {errorMessage && hasValidationError && (
          <p className="text-red-600 text-sm font-normal mt-2">
            {errorMessage}
          </p>
        )}
        {typingError && (
          <p className="text-red-600 font-light text-xs absolute">
            {typingError}
          </p>
        )}
        {(value?.length || !required) && infoText ? (
          <span className="text-gray-500">{infoText}</span>
        ) : null}
        {props.helper && (
          <div
            className={clsx(
              "text-sm leading-5 font-normal mt-2 text-gray-500",
              { "text-red-600": hasValidationError }
            )}
          >
            {props.helper}
          </div>
        )}
      </div>
    );
  }
);

Input.displayName = "Input";

export default Input;
